import { useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { fetchServerProgressionOverview } from "../api/progression";
import { fetchAllStars } from "../api/allStars";
import { fetchBossRankings } from "../api/rankings";
import type {
  ProgressionBossRequirement,
  ProgressionExpansionSummary,
  ProgressionObjectiveSummary,
  ProgressionRaceEntry,
  ProgressionTierSummary,
  ParseRecordSummary,
  AllStarStandingSummary,
} from "../api/types";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { RankingsSectionNav } from "../components/rankings/RankingsSectionNav";
import {
  getActiveServerSlug,
  serverAllStarsPath,
  serverBossProgressionPath,
  serverBossRankingMetricPath,
  serverCharacterPath,
  serverGuildPath,
  serverTierProgressionPath,
} from "../utils/siteContext";

function sortedExpansions(expansions: ProgressionExpansionSummary[]) {
  return [...expansions].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function displayExpansionName(expansion: ProgressionExpansionSummary) {
  return expansion.name === "Original EverQuest" ? "Classic" : expansion.name;
}

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort(
    (a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

function bossesFor(objective: ProgressionObjectiveSummary) {
  return objective.requiredBosses ?? objective.bosses ?? [];
}

function bossId(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBossId ??
    requirement.raidBoss?.id ??
    requirement.boss?.id ??
    requirement.id ??
    requirement.slug ??
    null
  );
}

function bossName(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.name ??
    requirement.boss?.name ??
    requirement.name ??
    "Unknown boss"
  );
}

function bossZone(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.raidZone?.name ??
    requirement.boss?.raidZone?.name ??
    requirement.zone ??
    null
  );
}
function bossSlug(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.slug ??
    requirement.boss?.slug ??
    requirement.slug ??
    null
  );
}

function toServerId(value: string | number | null | undefined) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function entryTime(entry: ProgressionRaceEntry) {
  return entry.completedAt ?? entry.killedAt ?? null;
}

function entryTimeMs(entry: ProgressionRaceEntry) {
  const time = entryTime(entry);
  const value = time ? Date.parse(time) : Number.POSITIVE_INFINITY;
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function allTierBosses(tier: ProgressionTierSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const objective of objectivesFor(tier)) {
    for (const boss of bossesFor(objective)) {
      const id = String(bossId(boss) ?? bossName(boss));
      if (!unique.has(id)) unique.set(id, boss);
    }
  }
  return [...unique.values()];
}

function guildLink(
  entry: ProgressionRaceEntry,
  className = "link-light fw-semibold text-decoration-none",
) {
  if (!entry.guild?.id)
    return (
      <span className="fw-semibold">
        {entry.guild?.name ?? "Unknown guild"}
      </span>
    );
  return (
    <Link to={serverGuildPath(getActiveServerSlug(window.location.pathname), entry.guild)} className={className}>
      {entry.guild.name}
    </Link>
  );
}

function rankBadgeClass(rank: number) {
  if (rank === 1) return "rank-badge rank-badge--gold";
  if (rank === 2) return "rank-badge rank-badge--silver";
  if (rank === 3) return "rank-badge rank-badge--bronze";
  return "rank-badge rank-badge--plain";
}

function RankBadge({ rank }: { rank: number }) {
  return <span className={rankBadgeClass(rank)}>#{rank}</span>;
}

function classShortName(className: string | null | undefined) {
  const normalized = className?.trim();
  if (!normalized) return "???";

  const shorthand: Record<string, string> = {
    Bard: "BRD",
    Beastlord: "BST",
    Berserker: "BER",
    Cleric: "CLR",
    Druid: "DRU",
    Enchanter: "ENC",
    Magician: "MAG",
    Monk: "MNK",
    Necromancer: "NEC",
    Paladin: "PAL",
    Ranger: "RNG",
    Rogue: "ROG",
    "Shadow Knight": "SHD",
    Shaman: "SHM",
    Warrior: "WAR",
    Wizard: "WIZ",
  };

  return shorthand[normalized] ?? normalized.slice(0, 3).toUpperCase();
}

interface TierRankingRow {
  guildId: string;
  guildName: string;
  serverName: string | null;
  killedBossIds: Set<string>;
  progressTimeMs: number;
  progressTime: string | null;
}

function tierRankingRows(tier: ProgressionTierSummary) {
  const rows = new Map<string, TierRankingRow>();
  for (const boss of allTierBosses(tier)) {
    const id = String(bossId(boss) ?? bossName(boss));
    for (const kill of boss.topKills ?? []) {
      const rawGuildId = kill.guild?.id;
      if (!rawGuildId) continue;
      const guildId = String(rawGuildId);
      const timeMs = entryTimeMs(kill);
      const existing = rows.get(guildId) ?? {
        guildId,
        guildName: kill.guild?.name ?? "Unknown guild",
        serverName: kill.server?.name ?? null,
        killedBossIds: new Set<string>(),
        progressTimeMs: Number.NEGATIVE_INFINITY,
        progressTime: null,
      };
      existing.killedBossIds.add(id);
      if (timeMs > existing.progressTimeMs && Number.isFinite(timeMs)) {
        existing.progressTimeMs = timeMs;
        existing.progressTime = entryTime(kill);
      }
      rows.set(guildId, existing);
    }
  }

  return [...rows.values()].sort((a, b) => {
    const progressDelta = b.killedBossIds.size - a.killedBossIds.size;
    if (progressDelta !== 0) return progressDelta;
    const aTime = Number.isFinite(a.progressTimeMs)
      ? a.progressTimeMs
      : Number.POSITIVE_INFINITY;
    const bTime = Number.isFinite(b.progressTimeMs)
      ? b.progressTimeMs
      : Number.POSITIVE_INFINITY;
    return aTime - bTime || a.guildName.localeCompare(b.guildName);
  });
}

function ProgressionRankingsTable({
  tier,
  serverSlug,
  expansionSlug,
}: {
  tier: ProgressionTierSummary;
  serverSlug: string;
  expansionSlug: string;
}) {
  const bosses = allTierBosses(tier);
  const rows = tierRankingRows(tier);
  const totalBosses = bosses.length;

  return (
    <div>
      <div className="mb-2">
        <h4 className="h5 mb-0">
          Progression
        </h4>
      </div>
      <div className="table-responsive">
        <table className="table table-dark table-sm table-hover align-middle mb-0 progression-race-table rankings-mini-table">
          <thead>
            <tr>
              <th className="text-body-secondary" style={{ width: "4rem" }}>
                Rank
              </th>
              <th>Guild</th>
              <th className="text-end">Progress</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.slice(0, 5).map((row, index) => (
                <tr key={row.guildId}>
                  <td>
                    <RankBadge rank={index + 1} />
                  </td>
                  <td className="fw-semibold">
                    <Link
                      to={serverGuildPath(getActiveServerSlug(window.location.pathname), { id: row.guildId, name: row.guildName })}
                      className="link-light text-decoration-none"
                    >
                      {row.guildName}
                    </Link>
                  </td>
                  <td className="text-end tabular-nums">
                    {row.killedBossIds.size}/{totalBosses}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="text-body-secondary py-3">
                  No boss kill records yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-end">
        <Link
          to={serverTierProgressionPath(serverSlug, expansionSlug)}
          className="small link-secondary"
        >
          All Rankings
        </Link>
      </div>
    </div>
  );
}

function BossCard({
  boss,
  serverSlug,
  expansionSlug,
}: {
  boss: ProgressionBossRequirement;
  serverSlug: string;
  expansionSlug: string;
}) {
  const kills = boss.topKills ?? [];
  const id = bossId(boss);
  const slug = bossSlug(boss);
  const linkRef = slug ?? id;
  return (
    <div className="col-12 col-md-6 col-xl-4 col-xxl-3">
      <article className="card h-100 border-secondary-subtle bg-body-tertiary progression-boss-card rankings-card">
        <div className="card-body d-flex flex-column">
          <div className="mb-3">
            <h5 className="card-title mb-1">{bossName(boss)}</h5>
            <div className="small text-body-secondary">
              {bossZone(boss) ?? "Unknown zone"}
            </div>
            {boss.isRequired === false ? (
              <span className="badge text-bg-secondary mt-2">Optional</span>
            ) : null}
          </div>
          <div className="table-responsive mb-3">
            <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
              <thead>
                <tr>
                  <th className="text-body-secondary" style={{ width: "4rem" }}>Rank</th>
                  <th>Guild</th>
                </tr>
              </thead>
              <tbody>
                {kills.length > 0 ? (
                  kills.slice(0, 5).map((kill, index) => (
                    <tr key={kill.id ?? `${kill.guild?.id ?? "guild"}-${index}`}>
                      <td><RankBadge rank={index + 1} /></td>
                      <td>{guildLink(kill)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-body-secondary py-3">No kills yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-auto pt-2 border-top border-secondary-subtle">
            {linkRef ? (
              <div className="d-flex flex-wrap gap-2">
                <Link
                  to={serverBossProgressionPath(serverSlug, linkRef, expansionSlug)}
                  className="small link-secondary"
                >
                  Progression
                </Link>
                <Link
                  to={serverBossRankingMetricPath(serverSlug, expansionSlug, linkRef, "DAMAGE")}
                  className="small link-secondary"
                >
                  Damage
                </Link>
                <Link
                  to={serverBossRankingMetricPath(serverSlug, expansionSlug, linkRef, "HEALING")}
                  className="small link-secondary"
                >
                  Healing
                </Link>
              </div>
            ) : (
              <span className="small text-body-secondary">
                Rankings unavailable
              </span>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}

function ProgressionCards({
  bosses,
  serverSlug,
  expansionSlug,
}: {
  bosses: ProgressionBossRequirement[];
  serverSlug: string;
  expansionSlug: string;
}) {
  return (
    <div>
      <div className="mb-2">
        <h4 className="h6 text-uppercase text-body-secondary mb-0">
          Progression
        </h4>
      </div>
      {bosses.length > 0 ? (
        <div className="row g-3">
          {bosses.map((boss, index) => (
            <BossCard
              boss={boss}
              serverSlug={serverSlug}
              expansionSlug={expansionSlug}
              key={bossId(boss) ?? index}
            />
          ))}
        </div>
      ) : (
        <div className="alert alert-secondary mb-0">
          No bosses configured for this tier.
        </div>
      )}
    </div>
  );
}

function formatRankingRate(
  parse: ParseRecordSummary,
  metric: "DAMAGE" | "HEALING",
) {
  const value = metric === "HEALING" ? parse.hps : parse.dps;
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function PlayerRankingCard({
  boss,
  rankings,
  metric,
  serverId,
  serverSlug,
  expansionSlug,
}: {
  boss: ProgressionBossRequirement;
  rankings: ParseRecordSummary[];
  metric: "DAMAGE" | "HEALING";
  serverId?: number;
  serverSlug: string;
  expansionSlug: string;
}) {
  const id = bossId(boss);
  const slug = bossSlug(boss);
  const linkRef = slug ?? id;
  const metricText = metric === "HEALING" ? "healing" : "damage";
  const bossKey = linkRef ? String(linkRef) : bossName(boss);
  const href = linkRef ? serverBossRankingMetricPath(serverSlug, expansionSlug, linkRef, metric) : null;

  return (
    <div className="col-12 col-md-6 col-xl-4 col-xxl-3">
      <article className="card h-100 border-secondary-subtle bg-body-tertiary progression-boss-card rankings-card">
        <div className="card-body d-flex flex-column">
          <div className="mb-3">
            <h5 className="card-title mb-1">{bossName(boss)}</h5>
            <div className="small text-body-secondary">
              {bossZone(boss) ?? "Unknown zone"}
            </div>
          </div>
          <div className="table-responsive mb-3">
            <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
              <thead>
                <tr>
                  <th className="text-body-secondary" style={{ width: "4rem" }}>Rank</th>
                  <th>Player</th>
                  <th className="text-body-secondary" style={{ width: "3.25rem" }}>Class</th>
                  <th className="text-end">{metric === "HEALING" ? "HPS" : "DPS"}</th>
                </tr>
              </thead>
              <tbody>
                {rankings.length > 0 ? (
                  rankings.slice(0, 5).map((parse, index) => (
                    <tr key={parse.id ?? `${bossKey}-${parse.characterId}-${index}`}>
                      <td><RankBadge rank={index + 1} /></td>
                      <td>
                        <Link
                          to={serverCharacterPath(getActiveServerSlug(window.location.pathname), { id: parse.characterId, name: parse.characterName })}
                          className="link-light fw-semibold text-decoration-none"
                        >
                          {parse.characterName}
                        </Link>
                      </td>
                      <td><span className="class-shorthand">{classShortName(parse.characterClass)}</span></td>
                      <td className="text-end tabular-nums text-nowrap">{formatRankingRate(parse, metric)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-body-secondary py-3">
                      No {metricText} parses yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-auto pt-2 border-top border-secondary-subtle">
            {href ? (
              <Link to={href} className="small link-secondary">
                All Rankings
              </Link>
            ) : (
              <span className="small text-body-secondary">
                Rankings unavailable
              </span>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}

function MetricBossCards({
  title,
  bosses,
  rankingsByBossId,
  metric,
  isLoading,
  serverId,
  serverSlug,
  expansionSlug,
}: {
  title: string;
  bosses: ProgressionBossRequirement[];
  rankingsByBossId: Map<string, ParseRecordSummary[]>;
  metric: "DAMAGE" | "HEALING";
  isLoading: boolean;
  serverId?: number;
  serverSlug: string;
  expansionSlug: string;
}) {
  return (
    <div>
      <div className="mb-2">
        <h4 className="h6 text-uppercase text-body-secondary mb-0">{title}</h4>
      </div>
      {isLoading ? (
        <div className="alert alert-secondary py-2 mb-3">
          Loading {title.toLowerCase()} rankings...
        </div>
      ) : null}
      {bosses.length > 0 ? (
        <div className="row g-3">
          {bosses.map((boss, index) => {
            const id = bossId(boss);
            const key = id ? String(id) : (bossSlug(boss) ?? bossName(boss));
            return (
              <PlayerRankingCard
                boss={boss}
                rankings={rankingsByBossId.get(String(id)) ?? []}
                metric={metric}
                serverId={serverId}
                serverSlug={serverSlug}
                expansionSlug={expansionSlug}
                key={`${metric}-${key}-${index}`}
              />
            );
          })}
        </div>
      ) : (
        <div className="alert alert-secondary mb-0">
          No bosses configured for this tier.
        </div>
      )}
    </div>
  );
}

function tierDefaultRankingPartition(tier: ProgressionTierSummary) {
  const partitions = tier.rankingPartitions ?? [];
  return partitions.find((partition) => partition.isDefault) ?? partitions[0] ?? null;
}

function tierDefaultRankingPartitionId(tier: ProgressionTierSummary) {
  const partition = tierDefaultRankingPartition(tier);
  return partition?.id === null || partition?.id === undefined ? null : String(partition.id);
}

function tierDefaultRankingPartitionSlug(tier: ProgressionTierSummary) {
  const partition = tierDefaultRankingPartition(tier);
  return partition?.slug || tier.slug || "default";
}

function formatAllStarPoints(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0.0";
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });
}

function AllStarRow({ standing, activeServerSlug }: { standing: AllStarStandingSummary; activeServerSlug: string }) {
  return (
    <tr>
      <td>
        <RankBadge rank={standing.rank} />
      </td>
      <td>
        <Link
          to={serverCharacterPath(activeServerSlug, standing.character)}
          className="link-light fw-semibold text-decoration-none"
        >
          {standing.character?.name ?? "Unknown player"}
        </Link>
      </td>
      <td><span className="class-shorthand">{classShortName(standing.character?.className)}</span></td>
      <td className="text-end tabular-nums">
        {formatAllStarPoints(standing.averagePercentile)}
      </td>
    </tr>
  );
}

function AllStarLeaderboardCard({
  title,
  metric,
  serverSlug,
  serverId,
  rankingPartitionId,
  expansionSlug,
  partitionSlug,
}: {
  title: string;
  metric: "DAMAGE" | "HEALING";
  serverSlug: string;
  serverId?: number;
  rankingPartitionId?: string | null;
  expansionSlug: string;
  partitionSlug: string;
}) {
  const query = useQuery({
    queryKey: [
      "progression",
      "all-stars-card",
      serverId,
      rankingPartitionId,
      metric,
    ],
    queryFn: () =>
      fetchAllStars({
        serverId,
        rankingPartitionId: rankingPartitionId ?? undefined,
        metric,
        limit: 5,
      }),
    enabled: Boolean(serverId && rankingPartitionId),
  });

  const standings = query.data?.standings ?? [];

  return (
    <article className="card h-100 border-secondary-subtle bg-body-tertiary rankings-card">
      <div className="card-body d-flex flex-column">
        <div className="mb-3">
          <h3 className="h5 mb-0">{title}</h3>
        </div>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
            <thead>
              <tr>
                <th className="text-body-secondary" style={{ width: "4rem" }}>
                  Rank
                </th>
                <th>Player</th>
                <th className="text-body-secondary" style={{ width: "3.25rem" }}>Class</th>
                <th className="text-end">Points</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr>
                  <td colSpan={4} className="text-body-secondary py-3">
                    Loading standings...
                  </td>
                </tr>
              ) : standings.length > 0 ? (
                standings
                  .slice(0, 5)
                  .map((standing) => (
                    <AllStarRow standing={standing} activeServerSlug={getActiveServerSlug(window.location.pathname)} key={standing.id} />
                  ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-body-secondary py-3">
                    No All-Star standings yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-auto pt-3 text-end">
          {serverId && rankingPartitionId ? (
            <Link
              to={serverAllStarsPath(serverSlug, {
                expansionSlug,
                partitionSlug,
                bossSlug: "all",
                metric,
              })}
              className="small link-secondary"
            >
              All Rankings
            </Link>
          ) : (
            <span className="small text-body-secondary">
              Rankings unavailable
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function ProgressionLeaderboardCard({
  tier,
  serverSlug,
  expansionSlug,
}: {
  tier: ProgressionTierSummary;
  serverSlug: string;
  expansionSlug: string;
}) {
  return (
    <article className="card h-100 border-secondary-subtle bg-body-tertiary rankings-card">
      <div className="card-body">
        <ProgressionRankingsTable tier={tier} serverSlug={serverSlug} expansionSlug={expansionSlug} />
      </div>
    </article>
  );
}

function TierSection({
  tier,
  serverSlug,
  serverId,
  expansionSlug,
}: {
  tier: ProgressionTierSummary;
  serverSlug: string;
  serverId?: number;
  expansionSlug: string;
}) {
  const objectives = objectivesFor(tier);
  const bosses = allTierBosses(tier);
  const rankingPartitionId = tierDefaultRankingPartitionId(tier);
  const rankingPartitionSlug = tierDefaultRankingPartitionSlug(tier);
  const rankingQueries = useQueries({
    queries: bosses.flatMap((boss) => {
      const id = bossId(boss);
      if (!id) return [];
      return [
        {
          queryKey: [
            "progression",
            "boss-rankings-card",
            serverId,
            id,
            "DAMAGE",
          ],
          queryFn: () =>
            fetchBossRankings(String(id), {
              serverId,
              metric: "DAMAGE",
              officialOnly: false,
              limit: 5,
            }),
          enabled: Boolean(serverId),
        },
        {
          queryKey: [
            "progression",
            "boss-rankings-card",
            serverId,
            id,
            "HEALING",
          ],
          queryFn: () =>
            fetchBossRankings(String(id), {
              serverId,
              metric: "HEALING",
              officialOnly: false,
              limit: 5,
            }),
          enabled: Boolean(serverId),
        },
      ];
    }),
  });

  const damageByBossId = new Map<string, ParseRecordSummary[]>();
  const healingByBossId = new Map<string, ParseRecordSummary[]>();
  let queryIndex = 0;
  for (const boss of bosses) {
    const id = bossId(boss);
    if (!id) continue;
    const damageResult = rankingQueries[queryIndex++];
    const healingResult = rankingQueries[queryIndex++];
    damageByBossId.set(String(id), damageResult?.data?.rankings ?? []);
    healingByBossId.set(String(id), healingResult?.data?.rankings ?? []);
  }
  const rankingsLoading = rankingQueries.some((query) => query.isLoading);

  return (
    <section className="ranking-tier-section mb-5">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h2 className="h3 mb-0">{tier.name}</h2>
        <span className="badge text-bg-dark border border-secondary-subtle">
          {bosses.length} bosses
        </span>
      </div>
      {objectives.length > 0 || bosses.length > 0 ? (
        <div className="d-grid gap-3">
          <section id="progression-rankings">
            <div className="mb-3">
              <h3 className="h4 mb-0">Leaderboards</h3>
            </div>
            <div className="row g-3 row-cols-1 row-cols-md-3">
              <div className="col">
                <ProgressionLeaderboardCard
                  tier={tier}
                  serverSlug={serverSlug}
                  expansionSlug={expansionSlug}
                />
              </div>
              <div className="col">
                <AllStarLeaderboardCard
                  title="DPS All-Stars"
                  metric="DAMAGE"
                  serverSlug={serverSlug}
                  serverId={serverId}
                  rankingPartitionId={rankingPartitionId}
                  expansionSlug={expansionSlug}
                  partitionSlug={rankingPartitionSlug}
                />
              </div>
              <div className="col">
                <AllStarLeaderboardCard
                  title="Healing All-Stars"
                  metric="HEALING"
                  serverSlug={serverSlug}
                  serverId={serverId}
                  rankingPartitionId={rankingPartitionId}
                  expansionSlug={expansionSlug}
                  partitionSlug={rankingPartitionSlug}
                />
              </div>
            </div>
          </section>

          <hr className="section-separator" />

          <section id="boss-rankings">
            <div className="mb-3">
              <h3 className="h4 mb-0">Tier Objectives</h3>
            </div>
            <div className="d-grid gap-4">
              <ProgressionCards bosses={bosses} serverSlug={serverSlug} expansionSlug={expansionSlug} />
              <MetricBossCards
                title="Damage"
                bosses={bosses}
                rankingsByBossId={damageByBossId}
                metric="DAMAGE"
                isLoading={rankingsLoading}
                serverId={serverId}
                serverSlug={serverSlug}
                expansionSlug={expansionSlug}
              />
              <MetricBossCards
                title="Healing"
                bosses={bosses}
                rankingsByBossId={healingByBossId}
                metric="HEALING"
                isLoading={rankingsLoading}
                serverId={serverId}
                serverSlug={serverSlug}
                expansionSlug={expansionSlug}
              />
            </div>
          </section>
        </div>
      ) : (
        <div className="alert alert-secondary">
          No progression content is configured for this tier.
        </div>
      )}
    </section>
  );
}

export function ProgressionPage() {
  const serverSlug = getActiveServerSlug();
  const location = useLocation();
  const activeRankingsSection = location.hash === "#progression-rankings"
    ? "progression"
    : location.hash === "#boss-rankings"
      ? "bosses"
      : "overview";
  const [selectedExpansionSlug, setSelectedExpansionSlug] = useState<
    string | null
  >(null);
  const query = useQuery({
    queryKey: ["progression-race-board", serverSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    enabled: true,
  });

  const progression = query.data?.progression;
  const server = query.data?.server;
  const expansions = useMemo(
    () => sortedExpansions(progression?.expansions ?? []),
    [progression],
  );
  const selectedExpansion = useMemo(() => {
    if (expansions.length === 0) return null;
    return (
      expansions.find(
        (expansion) => expansion.slug === selectedExpansionSlug,
      ) ?? expansions[0]
    );
  }, [expansions, selectedExpansionSlug]);

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            Rankings
            <span className="page-hero__server d-block">
              {server?.name ?? "Server"}
            </span>
          </h1>
        </div>
        {expansions.length > 0 && selectedExpansion ? (
          <div className="page-subnav rankings-expansion-subnav">
            <div className="content-wrapper">
              <ul
                className="nav secondary-tabs"
                aria-label="Expansion rankings"
              >
                {expansions.map((expansion) => (
                  <li className="nav-item" key={expansion.id}>
                    <button
                      type="button"
                      className={`nav-link ${selectedExpansion.slug === expansion.slug ? "active" : ""}`}
                      onClick={() => setSelectedExpansionSlug(expansion.slug)}
                    >
                      {displayExpansionName(expansion)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <RankingsSectionNav activeSection={activeRankingsSection} serverSlug={serverSlug} progressionExpansionSlug={selectedExpansion?.slug} />
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          {false ? (
            <div className="alert alert-secondary">
              Choose a server from the Rankings menu.
            </div>
          ) : null}

          {query.isLoading ? (
            <LoadingState label="Loading progression" />
          ) : null}
          {query.isError ? (
            <ErrorState
              title="Could not load progression"
              error={query.error}
            />
          ) : null}

          {query.data ? (
            expansions.length > 0 && selectedExpansion ? (
              <>
                {tiersFor(selectedExpansion).length > 0 ? (
                  tiersFor(selectedExpansion).map((tier, index, tiers) => (
                    <div key={tier.id}>
                      <TierSection
                        tier={tier}
                        serverSlug={serverSlug}
                        serverId={toServerId(server?.id)}
                        expansionSlug={selectedExpansion.slug}
                      />
                      {index < tiers.length - 1 ? (
                        <hr className="section-separator" />
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="alert alert-secondary">
                    No progression tiers are configured for this expansion.
                  </div>
                )}
              </>
            ) : (
              <div className="alert alert-secondary">
                No active progression data is configured for this server.{" "}
                <Link to="/admin/servers">Manage server raid content</Link>.
              </div>
            )
          ) : null}
        </div>
      </div>
    </>
  );
}
